/**
 * Interaction tracking: navigation (menu clicks that lead to a page) and
 * cta_button (elements opted in via `data-analytics-cta`). A single delegated
 * capture-phase click listener handles both.
 */
import { track } from '@theme/analytics';

/**
 * Classify a nav link by its container.
 * @param {Element} el
 * @returns {string|null}
 */
function navType(el) {
  if (el.closest('.jm-header-menu, header-menu, nav.menu-list')) return 'main_navigation';
  if (el.closest('footer, .footer, [class*="footer"]')) return 'footer_navigation';
  if (el.closest('header, header-component, .header')) return 'top_navigation';
  return null;
}

/**
 * Build the navigation_point label (prefixes the parent title for submenu links).
 * Icon/image links (e.g. the logo) have no text, so fall back to aria-label,
 * the image alt, the title, then a "Home" default for the logo.
 * @param {Element} link
 * @returns {string}
 */
function navPoint(link) {
  const text = (link.textContent || '').trim().replace(/\s+/g, ' ');
  if (text) {
    const topItem = link.closest('.menu-list__list-item');
    if (topItem && link.classList.contains('jm-subnav-link')) {
      const parent = topItem.querySelector('.menu-list__link-title');
      const parentText = parent ? (parent.textContent || '').trim() : '';
      if (parentText && parentText !== text) return `${parentText} > ${text}`;
    }
    return text;
  }
  const aria = (link.getAttribute('aria-label') || '').trim();
  if (aria) return aria;
  const img = link.querySelector('img[alt]');
  const alt = img ? (img.getAttribute('alt') || '').trim() : '';
  if (alt) return alt;
  const title = (link.getAttribute('title') || '').trim();
  if (title) return title;
  if (link.closest('.header__heading, [class*="logo"], #header-logo')) return 'Home';
  return '';
}

/**
 * Header controls that navigate/open but are NOT `<a href>` — the Account web
 * component and the cart drawer trigger button — so the link path above misses
 * them. Returns the navigation_point, or null.
 * @param {Element} el
 * @returns {string|null}
 */
function headerControlPoint(el) {
  if (el.closest('shopify-account, .account-button')) return 'Account';
  if (el.closest('[data-testid="cart-drawer-trigger"]')) return 'Cart';
  return null;
}

/** @param {MouseEvent} event */
function onClick(event) {
  const target = /** @type {Element} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;

  let navFired = false;
  const link = target.closest('a[href]');
  if (link) {
    const type = navType(link);
    const href = link.getAttribute('href') || '';
    const navigates = href && href !== '#' && !href.startsWith('javascript:');
    if (type && navigates) {
      track('navigation', { navigation_type: type, navigation_point: navPoint(link) });
      navFired = true;
    }
  }

  if (!navFired) {
    const point = headerControlPoint(target);
    if (point) track('navigation', { navigation_type: 'top_navigation', navigation_point: point });
  }

  const cta = target.closest('[data-analytics-cta]');
  if (cta) {
    const anchor = cta.matches('a[href]') ? cta : cta.closest('a[href]') || cta.querySelector('a[href]');
    const ctaText = cta.getAttribute('data-cta-text') || (cta.textContent || '').trim().replace(/\s+/g, ' ');
    const ctaLink = cta.getAttribute('data-cta-link') || (anchor && anchor.getAttribute('href')) || '';
    track('cta_button', { cta_text: ctaText, cta_link: ctaLink });
  }
}

document.addEventListener('click', onClick, { capture: true });
